import firebase from 'firebase';
import moment from 'moment';
import { serializer } from './shared/serializer';

import { EventsTypes } from '@/enums/EventsTypes';
import { EventsTypesMinor } from '@/enums/EventsTypesMinor';
import { ServiceStatus } from '@/enums/ServiceStatus';
import { ResidentStatus } from '@/enums/ResidentStatus';


interface Resident {
  id: string;
  dob: string;
  dateOfBirth: any;
  facilityId: string;
  facilityName: string;
  imageUrl: string;
  name: string;
  roomName: string;
  status: ResidentStatus;
}

const getResidentById = async (residentId: string) => {
  try {
    const resident = await firebase
      .firestore()
      .collection('residents')
      .doc(residentId)
      .get();

    return serializer.documentSnapshotToDocument<Resident>(resident)
  } catch (e) {
    console.error(`error getting resident by id: ${residentId}`, e)
  }
};

const getResidentPremiumServices = async ({ residentId, facilityId }) => {
  try {
    const premiumServices = await firebase
      .firestore()
      .collection('additionalServices')
      .where('facilityId', '==', facilityId)
      .where('membersIds', 'array-contains', residentId)
      .get();

    return serializer.querySnapshotToDocuments(premiumServices);
  } catch (e) {
    console.error(`error getting resident premium services: ${residentId}`, e);
  }
};

type GetResidentUpcomingEvents = {
  residentId: string;
  facilityId: string;
  period?: {
    start: Date;
    end: Date;
  };
};

const getResidentUpcomingEvents = async ({
  residentId,
  facilityId,
  period = {
    start: moment().toDate(),
    end: moment()
      .add(7, 'days')
      .toDate(),
  },
}: GetResidentUpcomingEvents) => {
  const premiumServicesIds = await (
    await getResidentPremiumServices({ residentId, facilityId })
  ).map(({ id }) => id);

  const events = serializer
    .querySnapshotToDocuments<any>(
      await firebase
        .firestore()
        .collection('calender')
        .where('facilityId', '==', facilityId)
        .where('start', '>=', period.start)
        .where('start', '<=', period.end)
        .orderBy('start')
        .limit(100)
        .get()
    )
    .filter(event => {
      const selectedEventsTypes = [
        EventsTypes.LifestylePrograms,
        EventsTypesMinor.ScheduledCall,
        EventsTypesMinor.ConciergeRequest,
      ];

      const isInvitedToPremiumServiceEvent = event.invitees?.some(invitee =>
        premiumServicesIds.includes(invitee)
      );

      const isInvitedToSelectedEventsTypes =
        selectedEventsTypes.includes(event.type) &&
        event.going?.includes(residentId);

      return isInvitedToPremiumServiceEvent || isInvitedToSelectedEventsTypes;
    });

  return serializer.transformFields(events, {
    start: start => moment(start.seconds * 1000).toDate(),
    end: end => moment(end.seconds * 1000).toDate(),
    header: (_, { start }) =>
      moment(start).isSame(moment(), 'day') ? 'Today' : 'This Week',
  });
};

type GetResidentPremiumServicesRequests = {
  residentId: string;
  facilityId: string;
  statuses: ServiceStatus[];
};

const getResidentPremiumServicesRequests = async ({
  residentId,
  facilityId,
  statuses,
}: GetResidentPremiumServicesRequests) => {
  let query = firebase
    .firestore()
    .collection('serviceRequests')
    .where('facilityId', '==', facilityId)
    .where('residentId', '==', residentId);

  if (statuses.length) {
    query = query.where('status', 'in', statuses);
  }

  const premiumServicesRequests = await query.get();

  return serializer.transformFields(
    serializer.querySnapshotToDocuments<any>(premiumServicesRequests),
    {
      createdDateTime: createdDateTime =>
        moment(createdDateTime.seconds * 1000).toDate(),
    }
  );
};

const getResidentActivePremiumServices = async ({
  residentId,
  facilityId,
}: {
  residentId: string;
  facilityId: string;
}) => {
  const activePremiumServices = await firebase
    .firestore()
    .collection('additionalServices')
    .where('facilityId', '==', facilityId)
    .where('membersIds', 'array-contains', residentId)
    .get();

  return serializer.querySnapshotToDocuments(activePremiumServices);
};

const updateResidentById = (residentId: string, data) => {
  return firebase
    .firestore()
    .collection('residents')
    .doc(residentId)
    .update(data);
};

const getFamilyMembersForResident = async ({
  residentId,
  facilityId,
}: {
  residentId: string;
  facilityId: string;
}) => {
  return serializer.querySnapshotToDocuments(
    await firebase
      .firestore()
      .collection('familyHubUsers')
      .where('facilityId', '==', facilityId)
      .where('residentIds', 'array-contains', residentId)
      .get()
  );
};

export const residents = {
  getResidentById,
  getResidentPremiumServices,
  getResidentUpcomingEvents,
  getResidentPremiumServicesRequests,
  getResidentActivePremiumServices,
  updateResidentById,
  getFamilyMembersForResident,
};
